<template>
    <form @submit.prevent="submit">
        <slot :form-fields="cookedFields" />
    </form>
</template>

<script>
import omit from "lodash/omit"
import * as validatorFunctions from "@vuelidate/validators"
import { useVuelidate } from "@vuelidate/core"

import FileTemplate from "~/components/elements/FileTemplate"
import FileGroupTemplate from "~/components/elements/FileGroupTemplate"

import InputCost from "~/components/inputs/InputCost"
import InputDate from "~/components/inputs/InputDate"
import InputNumber from "~/components/inputs/InputNumber"
import InputPassword from "~/components/inputs/InputPassword"
import InputRemote from "~/components/inputs/InputRemote"
import InputSelect from "~/components/inputs/InputSelect"
import InputSelectOther from "~/components/inputs/InputSelectOther"
import InputSwitch from "~/components/inputs/InputSwitch"
import InputText from "~/components/inputs/InputText"
import InputTextarea from "~/components/inputs/InputTextarea"
import RadioGroup from "~/components/inputs/RadioGroup"

import TermsAndConditions from "~/modules/login/components/TermsAndConditions"
import UploadButton from "~/components/buttons/UploadButton"

import { getValidators } from "~/lib/utils/validations"
import { notify } from "~/lib/bugsnag"

function getComponent(type) {
    if (!type) {
        // The component is defined in the template itself
        return null
    }

    switch (type) {
        case "text":
            return InputText
        case "textarea":
            return InputTextarea
        case "password":
            return InputPassword
        case "number":
            return InputNumber
        case "select":
        case "multi-select":
            return InputSelect
        case "select-other":
            return InputSelectOther
        case "radio-group":
            return RadioGroup
        case "date":
        case "daterange":
            return InputDate
        case "search":
            return InputRemote
        case "terms-and-conditions":
            return TermsAndConditions
        case "cost":
            return InputCost
        case "switch":
            return InputSwitch
        case "upload-button":
            return UploadButton
        case "file-template":
            return FileTemplate
        case "file-group-template":
            return FileGroupTemplate
        default:
            throw new Error(
                `Could not resolve component for field type "${type}"`
            )
    }
}

function updateOptionsSrc(optionsSrc = "", values) {
    let invalidOptionsSrc = false
    optionsSrc = optionsSrc.replace(/\${([\w_-]+)}/g, (_, prop) => {
        const val = values[prop]
        if (typeof val == "undefined" || val == null) {
            invalidOptionsSrc = true
        }
        return val
    })
    return invalidOptionsSrc ? null : optionsSrc
}

function getProps(field, values) {
    const { type, source, value, optionsSrc } = field
    let props = {}
    if (!type) {
        // The component is defined in the template itself
        return field
    }
    switch (type) {
        case "password":
        case "text":
        case "textarea":
        case "select":
        case "cost":
        case "switch":
        case "number":
        case "radio-group":
        case "rating":
        case "date":
            break
        case "upload-button":
            props.mode = "button"
            break
        case "file-template":
        case "file-group-template":
            props.showTemplate = true
            break
        case "select-other":
            props.optionsSrc = updateOptionsSrc(optionsSrc, values)
            break
        case "multi-select":
            props.multiple = true
            break
        case "search":
            props.endpoint = source
            break
        case "terms-and-conditions":
            props.disclaimerId = value
            break
        case "daterange":
            props.mode = "range"
            break
        default:
            throw new Error(`Unexpected field type ${type}`)
    }

    return {
        ...omit(field, "type"),
        ...props,
    }
}

export default {
    name: "ValidatableForm",

    props: {
        fields: Array,
        values: Object,
    },

    emits: ["submit"],

    expose: ["submit"],

    setup() {
        return {
            v$: useVuelidate(),
        }
    },

    computed: {
        cookedFields() {
            const fields = {}
            this.fields.forEach(field => {
                let { prop, title, type } = field
                fields[prop] = {
                    component: getComponent(type),
                    props: {
                        ...getProps(field, this.values),
                        status: this.getStatus(prop, title),
                    },
                }
            })
            return fields
        },
    },

    validations() {
        const rules = this.fields.reduce((validations, field) => {
            let { prop, rules = [], required } = field
            let validators = getValidators(
                required ? rules.concat("required") : rules
            )
            validations[prop] = {}
            validators.forEach(({ name, args }) => {
                try {
                    const validator = validatorFunctions[name]
                    if (name === "requiredIf") {
                        validations[prop][name] = validator(() =>
                            args(this.values)
                        )
                    } else {
                        validations[prop][name] = args
                            ? validator(args)
                            : validator
                    }
                } catch (error) {
                    notify(error)
                }
            })
            return validations
        }, {})
        return { values: rules }
    },

    methods: {
        getStatus(prop, name) {
            if (this.v$.values[prop].$error) {
                return {
                    code: "danger",
                    message: this.messageFor(name, this.v$.values[prop]),
                }
            }
        },

        messageFor(name, validationStatus) {
            const validations = Object.entries(validationStatus).filter(
                ([key]) => !key.startsWith("$")
            )

            for (const [param, validation] of validations) {
                if (validation.$response) continue

                switch (param) {
                    case "required":
                    case "requiredIf":
                        return this.$t("validation.required", {
                            attribute: name,
                        })

                    case "maxLength":
                        return this.$t("validation.max.string", {
                            attribute: name,
                            max: validation.$params.max,
                        })

                    case "minLength":
                        return this.$t("validation.min.string", {
                            attribute: name,
                            min: validation.$params.min,
                        })

                    case "url":
                        return this.$t("validation.url", {
                            attribute: name,
                        })

                    case "email":
                        return this.$t("validation.email", {
                            attribute: name,
                        })

                    default:
                        throw new Error(
                            `Error message for validator "${name}" doesn't exists`
                        )
                }
            }
        },

        submit() {
            this.v$.$touch()
            if (this.v$.$invalid) return
            this.$emit("submit")
            this.v$.$reset()
        },
    },
}
</script>
